import { useState } from 'react'
import { useTranslation } from '@/i18n'
import { ChevronDown, ChevronUp, Pencil, Check, X } from 'lucide-react'
import { Checkbox } from '@/components/ui/checkbox'
import type { EntityMatchCandidate } from '@/service/api'

export type Decision = 'accepted' | 'rejected' | null

interface Props {
  matches: EntityMatchCandidate[]
  selectedIds: Set<string>
  decisions: Record<string, Decision>
  onToggleSelect: (id: string) => void
  onToggleSelectAll: () => void
  onDecide: (id: string, decision: Decision) => void
}

// match_quality from the backend ('auto_accept' | 'manual_review' | 'reject')
// → the badge variant rendered in the Quality column. Anything unrecognized
// defaults to "Accept" (manual review).
type Quality = 'auto-accept' | 'accept' | 'reject'

const qualityFor = (m: EntityMatchCandidate): Quality => {
  const raw = (m.match_quality || '').toLowerCase()
  if (raw === 'auto_accept') return 'auto-accept'
  if (raw === 'reject') return 'reject'
  return 'accept'
}

// className is presentational and stays here; the user-facing label is
// resolved with t() at render time via QUALITY_BADGE_LABEL_KEY below.
const QUALITY_BADGE: Record<Quality, { className: string }> = {
  'auto-accept': {
    className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  },
  accept: {
    className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  },
  reject: {
    className: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  },
}

const QUALITY_BADGE_LABEL: Record<Quality, { key: string; defaultValue: string }> = {
  'auto-accept': { key: 'data_catalog:match_entity.quality_badge.auto_accept', defaultValue: 'Auto-Accept' },
  accept: { key: 'data_catalog:match_entity.quality_badge.accept', defaultValue: 'Accept' },
  reject: { key: 'data_catalog:match_entity.quality_badge.reject', defaultValue: 'Reject' },
}

// Synthetic per-row id. The backend response has no id field on candidates,
// so we key on pid_tag + ts_tag + index.
const rowId = (m: EntityMatchCandidate, i: number) => `${m.pid_tag}::${m.ts_tag}::${i}`

const MatchedFoundPanel = ({
  matches,
  selectedIds,
  decisions,
  onToggleSelect,
  onToggleSelectAll,
  onDecide,
}: Props) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(true)

  const allSelected = matches.length > 0 && matches.every((m, i) => selectedIds.has(rowId(m, i)))
  const someSelected = matches.some((m, i) => selectedIds.has(rowId(m, i))) && !allSelected

  return (
    <section className="rounded-lg border border-border bg-background dark:bg-dark-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
        aria-expanded={open}
      >
        <h2 className="text-sm font-semibold text-foreground">
          {t('match_entity:matched_found.title', { defaultValue: 'Matched Found' })}{' '}
          <span className="text-muted-foreground font-normal">({matches.length})</span>
        </h2>
        {open ? (
          <ChevronUp className="size-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="size-4 text-muted-foreground" />
        )}
      </button>

      {open && (
        <div className="border-t border-border overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-background">
              <tr className="border-b border-border text-left text-xs font-medium text-muted-foreground">
                <th className="w-10 px-4 py-3">
                  <Checkbox
                    checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                    onCheckedChange={onToggleSelectAll}
                    aria-label={t('match_entity:matched_found.select_all', { defaultValue: 'Select all' })}
                  />
                </th>
                <th className="px-3 py-3 font-medium">
                  {t('match_entity:matched_found.col.pid_tag', { defaultValue: 'P&ID Tag' })}
                </th>
                <th className="px-3 py-3 font-medium">
                  {t('match_entity:matched_found.col.ts_tag', { defaultValue: 'Time Series Tag' })}
                </th>
                <th className="px-3 py-3 font-medium">
                  {t('match_entity:matched_found.col.validation', { defaultValue: 'Validation' })}
                </th>
                <th className="px-3 py-3 font-medium">
                  {t('match_entity:matched_found.col.quality', { defaultValue: 'Quality' })}
                </th>
                <th className="px-3 py-3 font-medium">
                  {t('match_entity:matched_found.col.equipment', { defaultValue: 'Equipment' })}
                </th>
                <th className="w-20 px-3 py-3" />
              </tr>
            </thead>
            <tbody>
              {matches.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-12 text-center text-sm text-muted-foreground"
                  >
                    {t('match_entity:matched_found.empty', {
                      defaultValue: 'No matches yet — click Run Matching to discover candidates.',
                    })}
                  </td>
                </tr>
              ) : (
                matches.map((m, i) => {
                  const id = rowId(m, i)
                  const q = qualityFor(m)
                  const badge = QUALITY_BADGE[q]
                  const decision = decisions[id] ?? null
                  return (
                    <tr key={id} className="border-t border-border hover:bg-muted/40">
                      <td className="px-4 py-3">
                        <Checkbox
                          checked={selectedIds.has(id)}
                          onCheckedChange={() => onToggleSelect(id)}
                          aria-label={t('match_entity:matched_found.select_row', { defaultValue: 'Select row' })}
                        />
                      </td>
                      <td className="px-3 py-3 font-medium text-foreground">{m.pid_tag}</td>
                      <td className="px-3 py-3 font-medium text-foreground">{m.ts_tag}</td>
                      <td className="px-3 py-3 text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <Pencil className="size-3.5 shrink-0 text-muted-foreground" />
                          <span className="truncate max-w-[480px]">{m.reasoning}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}
                        >
                          {t(QUALITY_BADGE_LABEL[q].key, { defaultValue: QUALITY_BADGE_LABEL[q].defaultValue })}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-muted-foreground">
                        {m.equipment_type?.trim() || '—'}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => onDecide(id, decision === 'accepted' ? null : 'accepted')}
                            aria-pressed={decision === 'accepted'}
                            aria-label={t('match_entity:matched_found.accept', { defaultValue: 'Accept match' })}
                            className={`inline-flex size-7 items-center justify-center rounded-md ${
                              decision === 'accepted'
                                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                                : 'text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20'
                            }`}
                          >
                            <Check className="size-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => onDecide(id, decision === 'rejected' ? null : 'rejected')}
                            aria-pressed={decision === 'rejected'}
                            aria-label={t('match_entity:matched_found.reject', { defaultValue: 'Reject match' })}
                            className={`inline-flex size-7 items-center justify-center rounded-md ${
                              decision === 'rejected'
                                ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300'
                                : 'text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20'
                            }`}
                          >
                            <X className="size-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

export default MatchedFoundPanel
export { rowId }
